import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Modal, useToast, Button } from '@morressier/alembic';
import { FormProvider, useForm } from 'react-hook-form';
import { useOverwriteMinReviewers } from 'src/api/commands';
import { FormFieldNumber } from '../../../../FormField';

export const OverwriteReviewsModal = ({
  handleCloseModal,
  submissionId,
  submissionContextId,
  organizationId,
  refetchSubmission,
  defaultMinReviews
}: {
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
  refetchSubmission: () => void;
  handleCloseModal: () => void;
  defaultMinReviews?: number;
}) => {
  const toast = useToast();
  const methods = useForm<{ minReviewsTarget: number }>();
  const { mutateAsync } = useOverwriteMinReviewers(
    submissionId,
    submissionContextId,
    refetchSubmission,
    organizationId
  );

  const onSubmit = async ({
    minReviewsTarget
  }: {
    minReviewsTarget: number;
  }) => {
    try {
      await mutateAsync(minReviewsTarget);
      handleCloseModal();
      toast({
        variant: 'success',
        title: 'Review target updated',
        description: `The new target of ${minReviewsTarget} reviews has been set`,
        status: 'success'
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error updating review target',
        description: 'An error occurred while updating the review target',
        status: 'error'
      });
    }
  };

  return (
    <Modal
      isOpen
      onClose={handleCloseModal}
      width="45rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex flexDirection="column" gap={6} padding={8}>
            <Box>
              <FontAwesomeIcon fontSize={32} icon={faPenToSquare} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Overwrite Target</Text>
              <Text textStyle="textBody">
                Set a new target of reviewers needed for a complete evaluation
                of this submission.
              </Text>
            </Flex>

            <FormFieldNumber
              config={{
                name: 'minReviewsTarget',
                component: 'Custom:NumberInput',
                params: {
                  validations: {
                    kind: 'number',
                    required: true,
                    min: 0,
                    max: 99
                  },
                  labels: {
                    main: 'New Review Target',
                    subtitle: `The current default for this Article Type is ${defaultMinReviews}.`
                  }
                },
                caseDataFields: {
                  main: 'minReviewsTarget'
                }
              }}
            />

            <Flex justifyContent="flex-end" gap={2}>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
